import { useState, useContext, useEffect } from "react";
import * as AppUrls from "constants/AppUrls";
import { useHistory } from "react-router-dom";
import backArrow from "img/back-arrow.svg";
import { useQuery, useMutation, useQueryClient } from "react-query";
import DispatchContext from "context/DispatchContext";
import StateContext from "context/StateContext";
import { notificationMessage, checkIfAllowed } from "global/helpers";
import { useLocation } from "react-router-dom";
import { ItemCondition } from "./InventorySetup.model";
import { ErrorResponse } from "components/data/Errors.model";
import {
  getItemConditions,
  createItemCondition,
  editItemCondition,
  deleteItemCondition,
} from "./InventorySetup.services";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  CREATE_SUCCESS_MSG,
  UPDATE_SUCCESS_MSG,
  DELETE_SUCCESS_MSG,
} from "constants/NotificationMsgs";
const NewItemCondition = ({ match }) => {
  const [selectedId, setSelectedId] = useState<number>(null);
  const appDispatch = useContext(DispatchContext);
  const appState = useContext(StateContext);
  const queryClient = useQueryClient();
  const { error: errorLoadingData } = useQuery<any, ErrorResponse>(
    "get_conditions",
    getItemConditions
  );

  const formMethods = useForm({ mode: "onChange", reValidateMode: "onChange" });
  const {
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors, isDirty },
  } = formMethods;

  const location = useLocation();
  useEffect(() => {
    if (location.state) {
      const { id, name, description } = location.state;
      setSelectedId(id);
      setValue("name", name);
      setValue("description", description);
    }
  }, [location.state]);

  useEffect(() => {
    if (errorLoadingData?.errorMessage) {
      let notification = {
        variant: "danger",
        msg: errorLoadingData.errorMessage,
      };
      appDispatch({ type: "notification", value: notification });
    }
  }, [errorLoadingData, appDispatch]);

  const addCondition = useMutation(createItemCondition, {
    async onSuccess() {
      queryClient.invalidateQueries("get_conditions");
      let notification = {
        variant: "success",
        msg: `Condition ${CREATE_SUCCESS_MSG}`,
      };
      appDispatch({ type: "notification", value: notification });
      history.push(AppUrls.item_condition);
    },
    onError(error) {
      let notification = {
        variant: "danger",
        msg: notificationMessage(error, "problem adding condition"),
      };
      appDispatch({ type: "notification", value: notification });
    },
  });

  const updateCondition = useMutation(editItemCondition, {
    async onSuccess() {
      queryClient.invalidateQueries("get_conditions");
      let notification = {
        variant: "success",
        msg: `Information ${UPDATE_SUCCESS_MSG}`,
      };
      appDispatch({ type: "notification", value: notification });
      history.push(AppUrls.item_condition);
    },
    onError(error) {
      let notification = {
        variant: "danger",
        msg: notificationMessage(error, "problem editing condition"),
      };
      appDispatch({ type: "notification", value: notification });
    },
  });

  const removeItem = useMutation(deleteItemCondition, {
    async onSuccess() {
      queryClient.invalidateQueries("get_conditions");
      let notification = {
        variant: "success",
        msg: `Information ${DELETE_SUCCESS_MSG}`,
      };
      appDispatch({ type: "notification", value: notification });
    },
    onError(error) {
      let notification = {
        variant: "danger",
        msg: notificationMessage(error, "problem removing item condition"),
      };
      appDispatch({ type: "notification", value: notification });
    },
  });

  const history = useHistory();

  const onSubmit = (values) => {
    const data: ItemCondition = {
      itemConditionId: selectedId ? selectedId : 0,
      condition: values.name,
      description: values.description,
    };
    if (!selectedId) {
      addCondition.mutate(data);
    } else {
      updateCondition.mutate(data);
    }
  };

  const resetForm = () => {
    reset({ name: "", description: "" });
  };

  useEffect(() => {
    appDispatch({
      type: "loading",
      value:
        addCondition.isLoading ||
        updateCondition.isLoading ||
        removeItem.isLoading,
    });
  }, [
    addCondition.isLoading,
    updateCondition.isLoading,
    removeItem.isLoading,
    appDispatch,
  ]);

  return (
    <>
      <div className="static-page">
        <FormProvider {...formMethods}>
          <form id="new-method-form" onSubmit={handleSubmit(onSubmit)}>
            <div className="  page-title page-title-editable">
              <div className="d-flex flex-row">
                <div className="back-btn">
                  <img
                    src={backArrow}
                    alt="back arrow"
                    onClick={() => history.push(AppUrls.item_condition)}
                  />
                </div>
                <div>
                  <p className="">Item Condition</p>
                </div>
              </div>
              <div className="d-flex" style={{ marginLeft: "10px" }}>
                <button
                  type="button"
                  className="btn btn-outline-primary custom-outline custom-outline"
                  onClick={resetForm}
                >
                  Reset
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!isDirty}
                >
                  {!selectedId ? "Add Item Condition" : "Save Changes"}
                </button>
              </div>
            </div>
            <div className="page-content-wrapper">
              <div className="row h-100">
                {(checkIfAllowed(appState.allowedUrls, "add", match.path) ||
                  checkIfAllowed(appState.allowedUrls, "edit", match.path)) && (
                  <div className="row m-3">
                    <div className="col-4">
                      <Controller
                        control={control}
                        name="name"
                        render={({ field }) => (
                          <>
                            <label>
                              Item Condition{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              {...field}
                              type={"text"}
                              className={`form-control ${
                                errors.name ? "required_field" : ""
                              } `}
                              required={true}
                            />
                          </>
                        )}
                      />
                      {errors.name && errors.name.type === "maxLength" && (
                        <p className="text-danger">
                          Max length for name value must not exceed 15
                          characters.
                        </p>
                      )}
                      {errors.name && errors.name.type === "required" && (
                        <p className="text-danger">This field is required</p>
                      )}
                    </div>
                    <div className="col-4">
                      <label>
                        Description <span className="text-danger">*</span>
                      </label>
                      <Controller
                        control={control}
                        name="description"
                        render={({ field }) => (
                          <>
                            <input
                              {...field}
                              className={
                                errors.description
                                  ? "form-control required_field"
                                  : "form-control"
                              }
                              required={true}
                            />
                            {errors.description &&
                              errors.description.type === "required" && (
                                <p className="text-danger">
                                  This field is required
                                </p>
                              )}
                          </>
                        )}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>{" "}
          </form>
        </FormProvider>
      </div>
    </>
  );
};

export default NewItemCondition;
